import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, TILE_SIZE, COMPONENT_SPACING, EnemyType, EnemyGrade, ENEMY_STATS, ENEMY_TEXTURES } from '@/config/GameConfig';
import { EventBus, GameEvents } from '@/events/EventBus';
import { WaveManager } from '@/systems/WaveManager';
import { RobotManager } from '@/systems/RobotManager';
import { GameScene } from '@/scenes/GameScene';
import { Robot } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { JuiceEffects } from '@/systems/JuiceEffects';
import { DesignStore } from '@/systems/DesignStore';
import { SoundManager } from '@/systems/SoundManager';
import { drawRobotPreview } from '@/systems/ComponentIcons';
import { TECH, drawTechRect, fillTechRect, createTechButton, createSmallTechButton, showConfirmDialog, showTextInputDialog } from '@/ui/TechUI';
import { GOD, GOD_PARAMS, resetGodMode, saveGodMode, GodParam, getEnemyTypeScale, setEnemyTypeScale, resetEnemyTypeScales, getEnemyAnchor, setEnemyAnchor, exportFullProfileJSON, importFullProfile, loadPlayerMeta, savePlayerMeta, PlayerProfile, LAYER_PRESETS, DISPLAY_LAYERS, applyLayerPreset } from '@/config/GodMode';
import {
  ComponentType,
} from '@/components/ComponentDefs';
import { getCurve, setCurve, saveCurve } from '@/systems/WaveComposer';
import { t } from '@/i18n/I18n';

const CS = COMPONENT_SPACING;

export class GodModePanel {
  // God Mode panel
  private godPanel: Phaser.GameObjects.Container | null = null;
  private godPanelOpen = false;

  // Enemy scale editor
  private enemyScalePanel: Phaser.GameObjects.Container | null = null;

  // God Mode preview sandbox
  private godPreviewRobot: Robot | null = null;
  private godPreviewEnemy: Enemy | null = null;
  private godPreviewContainer: Phaser.GameObjects.Container | null = null;
  private godPreviewDesignIdx = 0;
  private godPreviewEnemyType: EnemyType = EnemyType.GRUNT;
  private godPreviewEnemyGrade: EnemyGrade = EnemyGrade.NORMAL;

  // Internal state
  private _activeGodTab: string = 'robot';
  private _godWheelHandler: ((p: Phaser.Input.Pointer, gx: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void) | null = null;
  private _godScrollMask: Phaser.GameObjects.Graphics | null = null;
  private _godScrollContainer: Phaser.GameObjects.Container | null = null;
  private _enemyScaleWheelHandler: ((p: Phaser.Input.Pointer, gx: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void) | null = null;
  private _enemyScaleMask: Phaser.GameObjects.Graphics | null = null;

  /** Callback invoked when GOD visual params change to refresh entities */
  private onRefreshVisuals: () => void;

  constructor(
    private scene: Phaser.Scene,
    private getGameScene: () => GameScene | undefined,
    private getRobotManager: () => RobotManager | undefined,
    private getWaveManager: () => WaveManager | undefined,
    onRefreshVisuals: () => void,
  ) {
    this.onRefreshVisuals = onRefreshVisuals;
  }

  get isOpen(): boolean { return this.godPanelOpen; }

  setup(): void {
    // No event listeners needed — UIScene handles GOD_PARAMS_CHANGED via onRefreshVisuals
  }

  destroy(): void {
    this.cleanupGodPreviews();
    this.closeEnemyScaleEditor();
    if (this._godWheelHandler) {
      this.scene.input.off('wheel', this._godWheelHandler);
      this._godWheelHandler = null;
    }
    if (this._godScrollMask) {
      this._godScrollMask.destroy();
      this._godScrollMask = null;
    }
    this.godPanel?.destroy();
    this.godPanel = null;
    this.godPanelOpen = false;
  }

  toggle(): void {
    if (this.godPanelOpen) {
      this.cleanupGodPreviews();
      this.closeEnemyScaleEditor();
      if (this._godWheelHandler) {
        this.scene.input.off('wheel', this._godWheelHandler);
        this._godWheelHandler = null;
      }
      if (this._godScrollMask) {
        this._godScrollMask.destroy();
        this._godScrollMask = null;
      }
      this.godPanel?.destroy();
      this.godPanel = null;
      this.godPanelOpen = false;
      // Resume game — GodMode paused it on open, so resume on close
      this.scene.scene.resume('GameScene');
      return;
    }
    this.godPanelOpen = true;
    this.scene.scene.pause('GameScene');
    this.buildGodPanel();
  }

  /** Force-refresh GOD visual params on preview entities */
  refreshGodVisuals(): void {
    // Refresh preview entities (no respawn — just update visuals in place)
    if (this.godPreviewRobot?.alive) { this.godPreviewRobot.invalidateBars(); this.godPreviewRobot.updateDebugOverlays(); }
    if (this.godPreviewEnemy?.alive) this.godPreviewEnemy.refreshGodVisuals();
  }

  /** Update preview entities in the game loop */
  updatePreviews(delta: number): void {
    // Update GOD preview enemy (stun flicker, cloak, sparkle animations)
    if (this.godPreviewEnemy?.alive) {
      this.godPreviewEnemy.update(delta);
    }

    // Update GOD preview robot stun (RobotPhysics doesn't run for preview robots)
    if (this.godPreviewRobot?.alive && this.godPreviewRobot.stunTimer > 0) {
      const dtS = delta / 1000;
      const robot = this.godPreviewRobot;
      robot.stunTimer = Math.max(0, robot.stunTimer - dtS);
      if (robot.stunTimer > 0) {
        robot.container.setAlpha(0.7 + 0.3 * Math.sin(robot.stunTimer * 8));
      }

      // Spinning stars just below energy bar
      if (!robot.stunStars) {
        robot.stunStars = this.scene.add.container(robot.x, robot.y).setDepth(robot.container.depth + 2);
        for (const ch of ['★', '✦', '★']) {
          robot.stunStars.add(this.scene.add.text(0, 0, ch, {
            fontSize: '16px', color: '#ffdd44', fontFamily: 'monospace',
            stroke: '#000000', strokeThickness: 3,
          }).setOrigin(0.5));
        }
      }
      // Compute energy bar Y (same as updateDebugOverlays)
      let topLocalY = 0;
      for (const c of robot.components) {
        const cy = c.localY * GOD.componentSpacingMult;
        if (cy < topLocalY) topLocalY = cy;
      }
      const actualScale = Math.abs(robot.container.scaleY);
      const halfComp = (COMPONENT_SPACING / 2) * actualScale;
      const topWorldOff = Math.abs(topLocalY) * actualScale + halfComp;
      const baseOff = (topWorldOff + 2) * GOD.robotBarOffsetMult;
      const enBarY = robot.y - baseOff + 3;
      robot.stunStars.setPosition(robot.x, enBarY + 6);
      robot.stunStarsAngle += dtS * 3;
      const children = robot.stunStars.getAll() as Phaser.GameObjects.Text[];
      for (let i = 0; i < children.length; i++) {
        const a = robot.stunStarsAngle + (Math.PI * 2 * i) / children.length;
        children[i].setPosition(Math.cos(a) * 16, Math.sin(a) * 6.4);
        children[i].setAlpha(0.7 + 0.3 * Math.sin(a * 2));
      }

      if (robot.stunTimer <= 0) {
        robot.stunStars?.destroy();
        robot.stunStars = null;
        robot.container.setAlpha(1);
      }
    }
  }

  private buildGodPanel(): void {
    if (this.godPanel) this.godPanel.destroy();

    const pw = 340;
    const categories = ['robot', 'enemy', 'economy', 'workshop', 'wave', 'display', 'scale', 'bars', 'popup', 'camera', 'cheat', 'billiard', 'jumper', 'profile'] as const;
    const catLabels: Record<string, string> = {
      robot: '⚙ ROBOT', enemy: '👾 ENEMY', economy: '💰 ECONOMY', workshop: '🔧 WORKSHOP', wave: '🌊 WAVE',
      display: '🖥 DISPLAY', scale: '📐 SCALE', bars: '📊 BARS', popup: '💬 POPUP', camera: '🎥 CAMERA', cheat: '⚡ CHEATS',
      billiard: '🎱 BILLIARD', jumper: '🦘 JUMPER', profile: '👤 PROFILE',
    };
    const catShortLabels: Record<string, string> = {
      robot: '⚙', enemy: '👾', economy: '💰', workshop: '🔧', wave: '🌊',
      display: '🖥', scale: '📐', bars: '📊', popup: '💬', camera: '🎥', cheat: '⚡',
      billiard: '🎱', jumper: '🦘', profile: '👤',
    };
    const catColors: Record<string, number> = {
      robot: 0x44aaff, enemy: 0xff6644, economy: 0xffcc00, workshop: 0x88ff44, wave: 0x44cc44,
      display: 0x66ddcc, scale: 0x88ccff, bars: 0x44ddaa, popup: 0xffaa66, camera: 0xaa88ff, cheat: 0xff44ff,
      billiard: 0x44ddff, jumper: 0x44dd88, profile: 0xff88ff,
    };

    const activeTab: string = this._activeGodTab ?? 'robot';
    this._activeGodTab = activeTab;

    const rowH = 22;
    const catHeaderH = 26;
    const presetBtnRowH = 20;
    const presetBtnRows = Math.ceil(LAYER_PRESETS.length / 4);
    const layerLabelH = 14;
    const layerBlockH = layerLabelH + presetBtnRows * presetBtnRowH + 4;
    const presetBlockH = DISPLAY_LAYERS.length * layerBlockH + 6;

    let contentH = 6 + catHeaderH + 8;
    if (activeTab === 'display') contentH += presetBlockH;
    if (activeTab === 'profile') {
      contentH += 200;
    } else {
      const params = GOD_PARAMS.filter(p => p.category === activeTab);
      contentH += params.length * rowH;
    }
    contentH += 20;

    const tabBarH = 48;
    const headerH = 32 + tabBarH;
    const maxPh = Math.floor(CANVAS_HEIGHT * 0.9);
    const ph = Math.min(contentH + headerH, maxPh);
    const needsScroll = contentH + headerH > ph;

    const panelX = CANVAS_WIDTH / 2;
    const panelY = CANVAS_HEIGHT / 2;
    this.godPanel = this.scene.add.container(panelX, panelY).setDepth(300);

    const dismiss = this.scene.add.rectangle(-panelX + CANVAS_WIDTH / 2, -panelY + CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.3)
      .setInteractive();
    dismiss.on('pointerdown', () => this.toggle());
    this.godPanel.add(dismiss);

    const bgGfx = this.scene.add.graphics();
    fillTechRect(bgGfx, -pw / 2, -ph / 2, pw, ph, 0x0a0a1a, 0.97);
    bgGfx.lineStyle(1, 0x1a1a2e, 0.5);
    bgGfx.strokeRect(-pw / 2 + 2, -ph / 2 + 2, pw - 4, ph - 4);
    drawTechRect(bgGfx, -pw / 2, -ph / 2, pw, ph, 0xff44ff, 0.5, 1.5);
    this.godPanel.add(bgGfx);

    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    this.godPanel.add(blocker);

    const titleText = this.scene.add.text(0, -ph / 2 + 14, t('godPanel.title'), {
      fontSize: '13px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.godPanel.add(titleText);

    const closeBtn = createSmallTechButton(this.scene, pw / 2 - 16, -ph / 2 + 14, '✕', TECH.DANGER, () => {
      SoundManager.play('clickCancel');
      this.toggle();
    });
    this.godPanel.add(closeBtn);

    // Tab bar
    const tabW = 42;
    const tabH = 18;
    const tabGap = 3;
    const perRow = 7;
    const tabRowStartY = -ph / 2 + 30;
    const totalTabRowW = perRow * tabW + (perRow - 1) * tabGap;
    const tabStartX = -totalTabRowW / 2 + tabW / 2;

    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i];
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const tx = tabStartX + col * (tabW + tabGap);
      const ty = tabRowStartY + row * (tabH + 3);
      const isActive = cat === activeTab;
      const color = catColors[cat];

      const tabGfx = this.scene.add.graphics();
      tabGfx.fillStyle(color, isActive ? 0.35 : 0.08);
      tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
      tabGfx.lineStyle(1, color, isActive ? 0.8 : 0.25);
      tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
      if (isActive) {
        tabGfx.fillStyle(color, 0.9);
        tabGfx.fillRect(tx - tabW / 2 + 4, ty + tabH / 2 - 2, tabW - 8, 2);
      }
      this.godPanel.add(tabGfx);

      const tabLabel = this.scene.add.text(tx, ty, catShortLabels[cat], {
        fontSize: '12px', fontFamily: 'monospace',
      }).setOrigin(0.5);
      tabLabel.setAlpha(isActive ? 1 : 0.5);
      this.godPanel.add(tabLabel);

      const tabHit = this.scene.add.rectangle(tx, ty, tabW, tabH, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      this.godPanel.add(tabHit);

      const tooltip = this.scene.add.text(tx, ty - tabH / 2 - 8, catLabels[cat].replace(/^. /, ''), {
        fontSize: '8px', color: '#' + color.toString(16).padStart(6, '0'),
        fontFamily: 'monospace', fontStyle: 'bold',
        backgroundColor: '#0a0a1aee', padding: { x: 4, y: 2 },
      }).setOrigin(0.5).setVisible(false).setDepth(310);
      this.godPanel.add(tooltip);

      tabHit.on('pointerover', () => {
        if (cat !== activeTab) {
          tabGfx.clear();
          tabGfx.fillStyle(color, 0.2);
          tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          tabGfx.lineStyle(1, color, 0.5);
          tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
        }
        tooltip.setVisible(true);
      });
      tabHit.on('pointerout', () => {
        if (cat !== activeTab) {
          tabGfx.clear();
          tabGfx.fillStyle(color, 0.08);
          tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          tabGfx.lineStyle(1, color, 0.25);
          tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
        }
        tooltip.setVisible(false);
      });
      tabHit.on('pointerdown', () => {
        if (cat === activeTab) return;
        SoundManager.play('click');
        this._activeGodTab = cat;
        this.buildGodPanel();
      });
    }

    const tabLine = this.scene.add.graphics();
    tabLine.lineStyle(1, 0xff44ff, 0.2);
    const lineY = -ph / 2 + headerH - 2;
    tabLine.lineBetween(-pw / 2 + 10, lineY, pw / 2 - 10, lineY);
    this.godPanel.add(tabLine);

    const scrollContainer = this.scene.add.container(0, 0);
    this.godPanel.add(scrollContainer);
    this._godScrollContainer = scrollContainer;

    let cy = -ph / 2 + headerH + 6;
    const leftX = -pw / 2 + 14;
    const trackLeft = 46;
    const trackW = 110;
    const valueX = pw / 2 - 20;

    const catColor = catColors[activeTab];
    const colorHex = '#' + catColor.toString(16).padStart(6, '0');

    const catBg = this.scene.add.graphics();
    catBg.fillStyle(catColor, 0.08);
    catBg.fillRoundedRect(-pw / 2 + 4, cy - 8, pw - 8, catHeaderH - 4, 4);
    catBg.lineStyle(1, catColor, 0.2);
    catBg.strokeRoundedRect(-pw / 2 + 4, cy - 8, pw - 8, catHeaderH - 4, 4);
    scrollContainer.add(catBg);

    const accent = this.scene.add.graphics();
    accent.fillStyle(catColor, 0.6);
    accent.fillRoundedRect(-pw / 2 + 6, cy - 4, 3, catHeaderH - 12, 1);
    scrollContainer.add(accent);

    scrollContainer.add(this.scene.add.text(leftX + 4, cy + 2, catLabels[activeTab], {
      fontSize: '10px', color: colorHex,
      fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
    cy += catHeaderH;

    if (activeTab === 'profile') {
      cy += 4;
      const scaleEditorBtn = createSmallTechButton(this.scene, 0, cy, t('godPanel.enemyScaleEditor'), 0xffaa44, () => {
        SoundManager.play('clickConfirm');
        this.openEnemyScaleEditor();
      });
      scrollContainer.add(scaleEditorBtn);

      cy += 28;
      const resetBtn = createSmallTechButton(this.scene, 0, cy, t('godPanel.resetAll'), TECH.DANGER, () => {
        showConfirmDialog(this.scene, t('god.resetConfirm'), () => {
          resetGodMode();
          this.buildGodPanel();
        });
      });
      scrollContainer.add(resetBtn);

      cy += 34;
      const profileHeader = this.scene.add.text(0, cy, t('godPanel.playerProfile'), {
        fontSize: '10px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5);
      scrollContainer.add(profileHeader);

      cy += 20;
      const meta = loadPlayerMeta();
      const nameLabel = this.scene.add.text(-pw / 2 + 16, cy, t('godPanel.name', { name: meta.name || t('godPanel.clickToSet') }), {
        fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      nameLabel.on('pointerdown', () => {
        showTextInputDialog(this.scene, 'Player name', meta.name || '', (name) => {
          meta.name = name; savePlayerMeta(meta); nameLabel.setText(t('godPanel.name', { name }));
        }, { maxLength: 20 });
      });
      scrollContainer.add(nameLabel);

      cy += 16;
      const ageLabel = this.scene.add.text(-pw / 2 + 16, cy, t('godPanel.age', { age: meta.age ?? t('godPanel.clickToSet') }), {
        fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      ageLabel.on('pointerdown', () => {
        showTextInputDialog(this.scene, 'Your age', String(meta.age ?? ''), (val) => {
          const age = parseInt(val, 10);
          if (!isNaN(age) && age > 0) { meta.age = age; savePlayerMeta(meta); ageLabel.setText(t('godPanel.age', { age })); }
        }, { maxLength: 3 });
      });
      scrollContainer.add(ageLabel);

      cy += 16;
      const expLevels: PlayerProfile['player']['experience'][] = ['beginner', 'casual', 'regular', 'experienced', 'hardcore'];
      const expLabel = this.scene.add.text(-pw / 2 + 16, cy, `Exp: ${meta.experience}`, {
        fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      expLabel.on('pointerdown', () => {
        const idx = (expLevels.indexOf(meta.experience) + 1) % expLevels.length;
        meta.experience = expLevels[idx];
        savePlayerMeta(meta);
        expLabel.setText(`Exp: ${meta.experience}`);
      });
      scrollContainer.add(expLabel);

      cy += 24;
      const exportBtn = createSmallTechButton(this.scene, 0, cy, t('godPanel.exportProfile'), 0x44ffaa, () => {
        SoundManager.play('clickConfirm');
        const profileJson = exportFullProfileJSON(
          SoundManager.exportSfxVolumes(),
          getCurve(),
        );
        const blob = new Blob([profileJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const playerName = loadPlayerMeta().name || 'player';
        a.href = url;
        a.download = `robogame_profile_${playerName.replace(/\s+/g, '_')}.json`;
        a.click();
        URL.revokeObjectURL(url);
      });
      scrollContainer.add(exportBtn);

      cy += 26;
      const importBtn = createSmallTechButton(this.scene, 0, cy, t('godPanel.importProfile'), 0xffaa44, () => {
        SoundManager.play('clickConfirm');
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => {
            const json = reader.result as string;
            const profile = importFullProfile(json);
            if (profile) {
              if (profile.sfxVolumes) SoundManager.importSfxVolumes(profile.sfxVolumes);
              if (profile.curveParams) { setCurve(profile.curveParams); saveCurve(); }
              SoundManager.play('evolve' as any);
              this.buildGodPanel();
            } else {
              alert('Invalid profile file.');
            }
          };
          reader.readAsText(file);
        };
        input.click();
      });
      scrollContainer.add(importBtn);
    } else {
      if (activeTab === 'display') {
        const btnW2 = 48;
        const btnH2 = 14;
        const perRow2 = 4;
        const gap = 4;
        const totalRowW = perRow2 * btnW2 + (perRow2 - 1) * gap;
        const startX = -totalRowW / 2 + btnW2 / 2;

        for (const layer of DISPLAY_LAYERS) {
          const layerHex = '#' + layer.color.toString(16).padStart(6, '0');
          scrollContainer.add(this.scene.add.text(leftX, cy + 4, `${layer.icon} ${layer.label}`, {
            fontSize: '8px', color: layerHex, fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0, 0.5));
          cy += layerLabelH;

          for (let i = 0; i < LAYER_PRESETS.length; i++) {
            const preset = LAYER_PRESETS[i];
            const col2 = i % perRow2;
            const row2 = Math.floor(i / perRow2);
            const bx = startX + col2 * (btnW2 + gap);
            const by = cy + row2 * presetBtnRowH;
            const pHex = '#' + preset.color.toString(16).padStart(6, '0');
            const btnGfx = this.scene.add.graphics();
            const drawBtn = (hover: boolean) => {
              btnGfx.clear();
              btnGfx.fillStyle(preset.color, hover ? 0.3 : 0.15);
              btnGfx.fillRoundedRect(bx - btnW2 / 2, by - btnH2 / 2, btnW2, btnH2, 3);
              btnGfx.lineStyle(1, preset.color, hover ? 0.7 : 0.4);
              btnGfx.strokeRoundedRect(bx - btnW2 / 2, by - btnH2 / 2, btnW2, btnH2, 3);
            };
            drawBtn(false);
            scrollContainer.add(btnGfx);
            scrollContainer.add(this.scene.add.text(bx, by, preset.name, {
              fontSize: '7px', color: pHex, fontFamily: 'monospace', fontStyle: 'bold',
            }).setOrigin(0.5));
            const hit = this.scene.add.rectangle(bx, by, btnW2, btnH2, 0x000000, 0)
              .setInteractive({ useHandCursor: true });
            scrollContainer.add(hit);
            const prefix = layer.prefix;
            hit.on('pointerdown', () => {
              SoundManager.play('click');
              applyLayerPreset(prefix, preset);
              EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
              this.buildGodPanel();
            });
            hit.on('pointerover', () => drawBtn(true));
            hit.on('pointerout', () => drawBtn(false));
          }
          cy += presetBtnRows * presetBtnRowH + 4;
        }
        cy += 6;
      }

      const params = GOD_PARAMS.filter(p => p.category === activeTab);
      for (const param of params) {
        this.addGodParamRow(param, leftX, cy, trackLeft, trackW, valueX, catColor);
        cy += rowH;
      }
    }

    cy += 20;

    // Scrolling
    if (needsScroll) {
      const maskShape = this.scene.make.graphics({});
      maskShape.fillStyle(0xffffff);
      maskShape.fillRect(
        panelX - pw / 2, panelY - ph / 2 + headerH,
        pw, ph - headerH,
      );
      const mask = maskShape.createGeometryMask();
      scrollContainer.setMask(mask);
      this._godScrollMask = maskShape;

      let scrollY = 0;
      const maxScroll = Math.max(0, contentH - (ph - headerH));

      const sbX = pw / 2 - 6;
      const sbTop = -ph / 2 + headerH + 2;
      const sbH = ph - headerH - 4;
      const thumbH = Math.max(20, ((ph - headerH) / contentH) * sbH);
      const sbTrack = this.scene.add.graphics();
      sbTrack.fillStyle(0x222233, 0.4);
      sbTrack.fillRoundedRect(sbX - 2, sbTop, 4, sbH, 2);
      this.godPanel.add(sbTrack);
      const sbThumb = this.scene.add.graphics();
      sbThumb.fillStyle(0xff88ff, 0.5);
      sbThumb.fillRoundedRect(sbX - 3, sbTop, 6, thumbH, 3);
      this.godPanel.add(sbThumb);

      const godWheelHandler = (p: Phaser.Input.Pointer, _gx: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
        const lx = p.x - panelX;
        const ly = p.y - panelY;
        if (Math.abs(lx) > pw / 2 || Math.abs(ly) > ph / 2) return;
        scrollY = Phaser.Math.Clamp(scrollY + dy * 0.5, 0, maxScroll);
        scrollContainer.y = -scrollY;
        const ratio = maxScroll > 0 ? scrollY / maxScroll : 0;
        const thumbY2 = sbTop + ratio * (sbH - thumbH);
        sbThumb.clear();
        sbThumb.fillStyle(0xff88ff, 0.5);
        sbThumb.fillRoundedRect(sbX - 3, thumbY2, 6, thumbH, 3);
      };
      this.scene.input.on('wheel', godWheelHandler);
      this._godWheelHandler = godWheelHandler;
    }

    this.buildGodPreviews();
  }

  private addGodParamRow(
    param: GodParam,
    labelX: number, y: number,
    trackLeft: number, trackW: number, valueX: number,
    catColor: number,
  ): void {
    if (!this.godPanel) return;
    const target = this._godScrollContainer ?? this.godPanel;

    target.add(this.scene.add.text(labelX, y, param.label, {
      fontSize: '9px', color: '#bbbbdd', fontFamily: 'monospace',
    }).setOrigin(0, 0.5));

    if (param.tip) {
      const helpX = trackLeft - 10;
      const helpIcon = this.scene.add.text(helpX, y, '?', {
        fontSize: '8px', color: '#666688', fontFamily: 'monospace', fontStyle: 'bold',
        backgroundColor: '#1a1a2e', padding: { x: 2, y: 0 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      target.add(helpIcon);

      const tipText = this.scene.add.text(helpX, y - 14, param.tip, {
        fontSize: '7px', color: '#ddddff', fontFamily: 'monospace',
        backgroundColor: '#1a1a3aee', padding: { x: 6, y: 4 },
        wordWrap: { width: 180 },
      }).setOrigin(0, 1).setVisible(false).setDepth(310);
      target.add(tipText);

      helpIcon.on('pointerover', () => { helpIcon.setColor('#ffcc44'); tipText.setVisible(true); });
      helpIcon.on('pointerout', () => { helpIcon.setColor('#666688'); tipText.setVisible(false); });
    }

    if (param.type === 'slider') {
      const min = param.min ?? 0;
      const max = param.max ?? 5;
      const currentVal = GOD[param.key] as number;
      const norm = (currentVal - min) / (max - min);

      const trackGfx = this.scene.add.graphics();
      trackGfx.fillStyle(0x222244, 0.6);
      trackGfx.fillRoundedRect(trackLeft, y - 2, trackW, 4, 2);
      const fillW = Math.max(2, norm * trackW);
      trackGfx.fillStyle(catColor, 0.35);
      trackGfx.fillRoundedRect(trackLeft, y - 2, fillW, 4, 2);
      target.add(trackGfx);

      const isInt = (max - min) > 10;
      const valText = this.scene.add.text(valueX, y, isInt ? String(Math.round(currentVal)) : currentVal.toFixed(1), {
        fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
      }).setOrigin(1, 0.5);
      target.add(valText);

      const knobX = trackLeft + norm * trackW;
      const knobGfx = this.scene.add.graphics();
      knobGfx.fillStyle(catColor, 0.7);
      knobGfx.fillCircle(knobX, y, 5);
      knobGfx.lineStyle(1.5, 0xffffff, 0.4);
      knobGfx.strokeCircle(knobX, y, 5);
      target.add(knobGfx);

      const knob = this.scene.add.rectangle(knobX, y, 14, 18, 0x000000, 0)
        .setInteractive({ useHandCursor: true, draggable: true });
      target.add(knob);
      this.scene.input.setDraggable(knob);

      const applySlider = (rawX: number) => {
        const clampedX = Math.max(trackLeft, Math.min(trackLeft + trackW, rawX));
        knob.x = clampedX;
        const n = (clampedX - trackLeft) / trackW;
        const step = param.step ?? 0.1;
        const raw = min + n * (max - min);
        const snapped = Math.round(raw / step) * step;
        (GOD as any)[param.key] = snapped;
        valText.setText(isInt ? String(Math.round(snapped)) : snapped.toFixed(1));
        knobGfx.clear();
        knobGfx.fillStyle(catColor, 0.7);
        knobGfx.fillCircle(clampedX, y, 5);
        knobGfx.lineStyle(1.5, 0xffffff, 0.4);
        knobGfx.strokeCircle(clampedX, y, 5);
        const newFillW = Math.max(2, n * trackW);
        trackGfx.clear();
        trackGfx.fillStyle(0x222244, 0.6);
        trackGfx.fillRoundedRect(trackLeft, y - 2, trackW, 4, 2);
        trackGfx.fillStyle(catColor, 0.35);
        trackGfx.fillRoundedRect(trackLeft, y - 2, newFillW, 4, 2);
        saveGodMode();
        EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
        this.onRefreshVisuals();
      };

      knob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applySlider(dragX));

      const trackHit = this.scene.add.rectangle(trackLeft + trackW / 2, y, trackW, 16, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      target.add(trackHit);
      trackHit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        applySlider(p.x - (this.godPanel?.x ?? 0));
      });

    } else if (param.type === 'toggle') {
      const currentVal = GOD[param.key] as boolean;

      const toggleW = 32;
      const toggleH = 14;
      const toggleX = trackLeft + 20;
      const toggleGfx = this.scene.add.graphics();
      const drawToggle = (on: boolean) => {
        toggleGfx.clear();
        toggleGfx.fillStyle(on ? 0x22664a : 0x222244, 0.8);
        toggleGfx.fillRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
        toggleGfx.lineStyle(1, on ? 0x44ff88 : 0x444466, 0.6);
        toggleGfx.strokeRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
        const kx = on ? toggleX + toggleW / 2 - toggleH / 2 : toggleX - toggleW / 2 + toggleH / 2;
        toggleGfx.fillStyle(on ? 0x44ff88 : 0x666688, 1);
        toggleGfx.fillCircle(kx, y, 4);
      };
      drawToggle(currentVal);
      target.add(toggleGfx);

      const toggleLabel = this.scene.add.text(toggleX + toggleW / 2 + 6, y, currentVal ? t('godPanel.on') : t('godPanel.off'), {
        fontSize: '8px', color: currentVal ? '#44ff88' : '#555566',
        fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      target.add(toggleLabel);

      const toggleHit = this.scene.add.rectangle(toggleX, y, toggleW + 12, toggleH + 6, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      target.add(toggleHit);

      toggleHit.on('pointerdown', () => {
        const newVal = !(GOD[param.key] as boolean);
        (GOD as any)[param.key] = newVal;
        drawToggle(newVal);
        toggleLabel.setText(newVal ? t('godPanel.on') : t('godPanel.off'));
        toggleLabel.setColor(newVal ? '#44ff88' : '#555566');
        saveGodMode();
        SoundManager.play('click');
        EventBus.emit(GameEvents.GOD_PARAMS_CHANGED);
        this.onRefreshVisuals();
      });
    }
  }

  // ── Enemy Scale Editor ──

  private openEnemyScaleEditor(): void {
    if (this.enemyScalePanel) {
      this.closeEnemyScaleEditor();
      return;
    }

    const pw = 700;
    const enemyTypes = Object.values(EnemyType);
    const cols = 5;
    const rows = Math.ceil(enemyTypes.length / cols);
    const cellW = 120;
    const cellH = 140;
    const gridH = rows * cellH;
    const headerH2 = 36;
    const footerH = 30;
    const contentH = headerH2 + gridH + footerH;
    const maxPh = Math.floor(CANVAS_HEIGHT * 0.9);
    const ph = Math.min(contentH, maxPh);
    const needsScroll = contentH > ph;

    const cx = CANVAS_WIDTH / 2;
    const cy = CANVAS_HEIGHT / 2;

    this.enemyScalePanel = this.scene.add.container(cx, cy).setDepth(310);

    const dismiss = this.scene.add.rectangle(-cx + CANVAS_WIDTH / 2, -cy + CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.5)
      .setInteractive();
    dismiss.on('pointerdown', () => this.closeEnemyScaleEditor());
    this.enemyScalePanel.add(dismiss);

    const bgGfx = this.scene.add.graphics();
    fillTechRect(bgGfx, -pw / 2, -ph / 2, pw, ph, TECH.BG, 0.97);
    drawTechRect(bgGfx, -pw / 2, -ph / 2, pw, ph, 0xffaa44, 0.7, 1.5);
    this.enemyScalePanel.add(bgGfx);

    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    this.enemyScalePanel.add(blocker);

    this.enemyScalePanel.add(this.scene.add.text(0, -ph / 2 + 12, t('godPanel.enemyScaleEditor'), {
      fontSize: '13px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    const closeBtn = createSmallTechButton(this.scene, pw / 2 - 16, -ph / 2 + 14, '✕', TECH.DANGER, () => {
      SoundManager.play('clickCancel');
      this.closeEnemyScaleEditor();
    });
    this.enemyScalePanel.add(closeBtn);

    const scrollCont = this.scene.add.container(0, 0);
    this.enemyScalePanel.add(scrollCont);

    const gridStartX = -((Math.min(cols, enemyTypes.length)) * cellW) / 2;
    const gridStartY = -ph / 2 + headerH2;

    for (let i = 0; i < enemyTypes.length; i++) {
      const eType = enemyTypes[i];
      const col2 = i % cols;
      const row2 = Math.floor(i / cols);
      const cellCx = gridStartX + col2 * cellW + cellW / 2;
      const cellCy = gridStartY + row2 * cellH + cellH / 2 - 10;

      this.buildEnemyScaleCell(scrollCont, eType, cellCx, cellCy, cellW, cellH);
    }

    const resetY = gridStartY + gridH + 6;
    const resetBtn = createSmallTechButton(this.scene, 0, resetY, t('godPanel.resetScales'), TECH.DANGER, () => {
      showConfirmDialog(this.scene, t('godPanel.resetScalesConfirm'), () => {
        resetEnemyTypeScales();
        this.closeEnemyScaleEditor();
        this.openEnemyScaleEditor();
        this.onRefreshVisuals();
      });
    });
    scrollCont.add(resetBtn);

    if (needsScroll) {
      const maskShape = this.scene.make.graphics({});
      maskShape.fillStyle(0xffffff);
      maskShape.fillRect(cx - pw / 2, cy - ph / 2 + headerH2, pw, ph - headerH2);
      scrollCont.setMask(maskShape.createGeometryMask());
      this._enemyScaleMask = maskShape;

      let scrollY = 0;
      const maxScroll = Math.max(0, contentH - ph + headerH2);
      const wheelHandler = (p: Phaser.Input.Pointer, _gx: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
        const lx = p.x - cx;
        const ly = p.y - cy;
        if (Math.abs(lx) > pw / 2 || Math.abs(ly) > ph / 2) return;
        scrollY = Phaser.Math.Clamp(scrollY + dy * 0.5, 0, maxScroll);
        scrollCont.y = -scrollY;
      };
      this.scene.input.on('wheel', wheelHandler);
      this._enemyScaleWheelHandler = wheelHandler;
    }
  }

  private buildEnemyScaleCell(
    container: Phaser.GameObjects.Container,
    eType: EnemyType,
    cellCx: number, cellCy: number,
    cellW: number, _cellH: number,
  ): void {
    const stats = ENEMY_STATS[eType];
    if (!stats) return;
    const texKey = ENEMY_TEXTURES[eType];

    const cellBg = this.scene.add.graphics();
    cellBg.lineStyle(1, 0x444466, 0.4);
    cellBg.strokeRect(cellCx - cellW / 2 + 4, cellCy - 50, cellW - 8, 110);
    container.add(cellBg);

    const tileRef = this.scene.add.rectangle(cellCx, cellCy - 8, TILE_SIZE, TILE_SIZE, 0x333344, 0.3)
      .setStrokeStyle(1, 0x555577, 0.5);
    container.add(tileRef);

    const currentScale = getEnemyTypeScale(eType);
    const displaySize = Math.max(50, stats.radius * 8) * GOD.enemyScaleMult * currentScale;
    const sprite = this.scene.add.image(cellCx, cellCy - 8, texKey);
    const maxDim = Math.max(sprite.width, sprite.height);
    sprite.setScale(displaySize / maxDim);
    container.add(sprite);

    const label = stats.label || eType;
    container.add(this.scene.add.text(cellCx, cellCy - 48, label.toUpperCase(), {
      fontSize: '8px', color: '#' + stats.color.toString(16).padStart(6, '0'),
      fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    const valText = this.scene.add.text(cellCx, cellCy + 28, currentScale.toFixed(2) + 'x', {
      fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
    }).setOrigin(0.5);
    container.add(valText);

    const trackW2 = cellW - 30;
    const trackX = cellCx;
    const trackY = cellCy + 42;
    const track = this.scene.add.rectangle(trackX, trackY, trackW2, 3, TECH.DIM);
    container.add(track);

    const sliderMin = 0.2;
    const sliderMax = 3.0;
    const norm = (currentScale - sliderMin) / (sliderMax - sliderMin);
    const knobMinX = trackX - trackW2 / 2;
    const knobX = knobMinX + norm * trackW2;

    const knob = this.scene.add.rectangle(knobX, trackY, 8, 14, 0x000000, 0)
      .setStrokeStyle(1.5, 0xffaa44)
      .setInteractive({ useHandCursor: true, draggable: true });
    container.add(knob);
    this.scene.input.setDraggable(knob);

    const applySlider = (rawX: number) => {
      const clampedX = Phaser.Math.Clamp(rawX, knobMinX, knobMinX + trackW2);
      knob.x = clampedX;
      const n = (clampedX - knobMinX) / trackW2;
      const raw = sliderMin + n * (sliderMax - sliderMin);
      const snapped = Math.round(raw * 20) / 20;
      setEnemyTypeScale(eType, snapped);
      valText.setText(snapped.toFixed(2) + 'x');

      const newDisplay = Math.max(50, stats.radius * 8) * GOD.enemyScaleMult * snapped;
      sprite.setScale(newDisplay / maxDim);

      this.onRefreshVisuals();
    };

    knob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applySlider(dragX));

    const trackHit = this.scene.add.rectangle(trackX, trackY, trackW2, 16, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(trackHit);
    trackHit.on('pointerdown', (p: Phaser.Input.Pointer) => {
      applySlider(p.x - (this.enemyScalePanel?.x ?? 0));
    });

    // Projectile anchor
    const anchorData = getEnemyAnchor(eType);
    const spriteCx = cellCx;
    const spriteCy = cellCy - 8;
    const maxOff = 30;

    const anchorGfx = this.scene.add.graphics();
    const drawCrosshair = (ax: number, ay: number) => {
      anchorGfx.clear();
      anchorGfx.lineStyle(1.5, 0xff4444, 0.9);
      anchorGfx.beginPath();
      anchorGfx.moveTo(ax - 7, ay); anchorGfx.lineTo(ax + 7, ay);
      anchorGfx.moveTo(ax, ay - 7); anchorGfx.lineTo(ax, ay + 7);
      anchorGfx.strokePath();
      anchorGfx.lineStyle(1, 0xff4444, 0.4);
      anchorGfx.strokeCircle(ax, ay, 5);
    };
    drawCrosshair(spriteCx + anchorData.ox, spriteCy + anchorData.oy);
    container.add(anchorGfx);

    const anchorLabel = this.scene.add.text(cellCx, cellCy + 54, `\u2295 ${anchorData.ox.toFixed(0)},${anchorData.oy.toFixed(0)}`, {
      fontSize: '7px', color: '#ff6666', fontFamily: 'monospace',
    }).setOrigin(0.5);
    container.add(anchorLabel);

    const clickZoneSize = TILE_SIZE + 20;
    const clickZone = this.scene.add.rectangle(spriteCx, spriteCy, clickZoneSize, clickZoneSize, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(clickZone);

    const placeAnchor = (pointer: Phaser.Input.Pointer) => {
      const panel = this.enemyScalePanel;
      if (!panel) return;
      const panelPosX = panel.x;
      const panelPosY = panel.y;
      const scrollYOff = container.y;

      const localX = Phaser.Math.Clamp(
        Math.round(pointer.x - panelPosX - spriteCx),
        -maxOff, maxOff,
      );
      const localY = Phaser.Math.Clamp(
        Math.round(pointer.y - panelPosY - scrollYOff - spriteCy),
        -maxOff, maxOff,
      );

      drawCrosshair(spriteCx + localX, spriteCy + localY);
      anchorLabel.setText(`\u2295 ${localX},${localY}`);
      setEnemyAnchor(eType, localX, localY);
    };

    clickZone.on('pointerdown', placeAnchor);
  }

  private closeEnemyScaleEditor(): void {
    if (this._enemyScaleWheelHandler) {
      this.scene.input.off('wheel', this._enemyScaleWheelHandler);
      this._enemyScaleWheelHandler = null;
    }
    if (this._enemyScaleMask) {
      this._enemyScaleMask.destroy();
      this._enemyScaleMask = null;
    }
    this.enemyScalePanel?.destroy();
    this.enemyScalePanel = null;
  }

  // ── GOD Mode Preview Sandbox ──

  private static readonly GOD_PREVIEW_DESIGNS: { name: string; components: { type: ComponentType; lx: number; ly: number; rotation?: number }[] }[] = [
    { name: 'Turret', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS },
      { type: ComponentType.CANNON_LIGHT, lx: CS, ly: 0 },
    ]},
    { name: 'Heavy Tank', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.ARMOR, lx: -CS, ly: 0 },
      { type: ComponentType.ARMOR, lx: CS, ly: 0 },
      { type: ComponentType.CANNON_HEAVY, lx: 0, ly: -CS },
      { type: ComponentType.BATTERY, lx: 0, ly: CS },
      { type: ComponentType.WHEEL, lx: -CS, ly: CS },
      { type: ComponentType.WHEEL, lx: CS, ly: CS },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS * 2 },
    ]},
    { name: 'Scout', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.WHEEL, lx: -CS, ly: 0 },
      { type: ComponentType.WHEEL, lx: CS, ly: 0 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: -CS },
    ]},
    { name: 'Burst DPS', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.BURST_CANNON, lx: -CS, ly: -CS },
      { type: ComponentType.BURST_CANNON, lx: CS, ly: -CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: CS, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -CS },
    ]},
    { name: 'Flyer', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.WING, lx: -CS, ly: 0 },
      { type: ComponentType.WING, lx: CS, ly: 0, rotation: Math.PI },
      { type: ComponentType.CANNON_LIGHT, lx: 0, ly: -CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: -CS },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -CS * 2 },
    ]},
    { name: 'Missile SAM', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.MISSILE_SAM, lx: 0, ly: -CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.BATTERY, lx: CS, ly: 0 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -CS * 2 },
    ]},
    { name: 'Medic', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.REPAIR_MODULE, lx: 0, ly: -CS },
      { type: ComponentType.RECHARGE_MODULE, lx: 0, ly: CS },
      { type: ComponentType.BATTERY, lx: -CS, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: CS, ly: 0 },
    ]},
    { name: 'Bomber', components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.EXPLOSIVE_CHARGE, lx: 0, ly: -CS },
      { type: ComponentType.THRUSTER, lx: 0, ly: CS },
      { type: ComponentType.SENSOR_SHORT, lx: CS, ly: 0 },
      { type: ComponentType.ARMOR, lx: -CS, ly: 0 },
    ]},
  ];

  private getPreviewDesigns(): { name: string; components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[] }[] {
    const designs = [...GodModePanel.GOD_PREVIEW_DESIGNS];
    for (const sd of DesignStore.getAll()) {
      designs.push({ name: sd.name, components: sd.design.components });
    }
    return designs;
  }

  private static readonly PREVIEW_SCALE = 2.5;

  private get previewRobotX(): number { return CANVAS_WIDTH / 2 - 170 - 100; }
  private get previewEnemyX(): number { return CANVAS_WIDTH / 2 + 170 + 100; }
  private get previewY(): number { return CANVAS_HEIGHT / 2 - 60; }

  private buildGodPreviews(): void {
    this.cleanupGodPreviews();
    this.godPreviewContainer = this.scene.add.container(0, 0).setDepth(301);

    const cy = CANVAS_HEIGHT / 2;
    const robotX = this.previewRobotX;
    const enemyX = this.previewEnemyX;

    // Left: Robot preview
    this.godPreviewContainer.add(this.scene.add.text(robotX, cy - 200, t('godPanel.robotPreview'), {
      fontSize: '10px', color: '#44aaff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    this.spawnPreviewRobot(robotX, cy - 60);
    this.buildCycleSelector(robotX, cy - 170, () => {
      const designs = this.getPreviewDesigns();
      return designs[this.godPreviewDesignIdx]?.name ?? 'N/A';
    }, (dir) => {
      const designs = this.getPreviewDesigns();
      if (designs.length === 0) return;
      this.godPreviewDesignIdx = (this.godPreviewDesignIdx + dir + designs.length) % designs.length;
      this.respawnPreviewRobot();
    });

    const rbtnX = robotX;
    let rbtnY = cy + 80;
    const btnGap = 36;

    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.damage'), TECH.DANGER, () => {
      if (!this.godPreviewRobot?.alive) return;
      this.godPreviewRobot.takeDamage(15);
      SoundManager.play('hit');
    });
    rbtnY += btnGap;
    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.repair'), 0x44ff88, () => {
      if (!this.godPreviewRobot?.alive) return;
      const broken = this.godPreviewRobot.components.find(c => c.broken);
      if (broken) {
        this.godPreviewRobot.repairComponent(broken);
      }
      this.godPreviewRobot.resetHpEnergy();
      SoundManager.play('repair');
    });
    rbtnY += btnGap;
    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.stun'), TECH.ACCENT, () => {
      if (!this.godPreviewRobot?.alive) return;
      this.godPreviewRobot.applyStun(2);
    });
    rbtnY += btnGap;
    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.attack'), 0xff8800, () => {
      if (!this.godPreviewRobot?.alive) return;
      const robot = this.godPreviewRobot;
      const weapons = robot.getWeapons();
      if (weapons.length === 0) return;
      for (const w of weapons) {
        const wPos = robot.getWeaponWorldPos(w);
        const targetX = wPos.x + 60;
        const targetY = wPos.y + (Math.random() - 0.5) * 20;
        JuiceEffects.muzzleFlash(this.scene, wPos.x, wPos.y, w.def.color ?? 0xffaa00);
        const trail = this.scene.add.graphics().setDepth(26);
        trail.lineStyle(2, w.def.color ?? 0xffaa00, 0.8);
        trail.lineBetween(wPos.x, wPos.y, targetX, targetY);
        trail.fillStyle(0xffffff, 0.9);
        trail.fillCircle(targetX, targetY, 3);
        this.scene.tweens.add({
          targets: trail, alpha: 0,
          duration: 250, onComplete: () => trail.destroy(),
        });
      }
      SoundManager.play('shoot');
      JuiceEffects.screenShake(this.scene, 0.002, 50);
    });
    rbtnY += btnGap;
    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.destroy'), TECH.DANGER, () => {
      if (!this.godPreviewRobot?.alive) return;
      this.godPreviewRobot.destroy();
      SoundManager.play('robotExplode');
    });
    rbtnY += btnGap;
    this.addPreviewButton(rbtnX, rbtnY, t('godPanel.respawn'), TECH.COLOR, () => {
      this.respawnPreviewRobot();
    });

    // Right: Enemy preview
    this.godPreviewContainer.add(this.scene.add.text(enemyX, cy - 200, t('godPanel.enemyPreview'), {
      fontSize: '10px', color: '#ff6644', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    const enemyTypes = Object.values(EnemyType);
    const enemyGrades = Object.values(EnemyGrade);

    this.spawnPreviewEnemy(enemyX, cy - 60);

    this.buildCycleSelector(enemyX, cy - 170, () => {
      return this.godPreviewEnemyType.toUpperCase();
    }, (dir) => {
      const idx = enemyTypes.indexOf(this.godPreviewEnemyType);
      this.godPreviewEnemyType = enemyTypes[(idx + dir + enemyTypes.length) % enemyTypes.length];
      this.respawnPreviewEnemy();
    });

    this.buildCycleSelector(enemyX, cy - 145, () => {
      return this.godPreviewEnemyGrade.toUpperCase();
    }, (dir) => {
      const idx = enemyGrades.indexOf(this.godPreviewEnemyGrade);
      this.godPreviewEnemyGrade = enemyGrades[(idx + dir + enemyGrades.length) % enemyGrades.length];
      this.respawnPreviewEnemy();
    });

    let ebtnY = cy + 80;
    this.addPreviewButton(enemyX, ebtnY, t('godPanel.damage'), TECH.DANGER, () => {
      if (!this.godPreviewEnemy?.alive) return;
      const dmg = Math.max(1, Math.ceil(this.godPreviewEnemy.maxHp * 0.3));
      this.godPreviewEnemy.takeDamage(dmg);
      SoundManager.play('hit');
    });
    ebtnY += btnGap;
    this.addPreviewButton(enemyX, ebtnY, t('godPanel.stun'), TECH.ACCENT, () => {
      if (!this.godPreviewEnemy?.alive) return;
      this.godPreviewEnemy.stun(2);
    });
    ebtnY += btnGap;
    this.addPreviewButton(enemyX, ebtnY, t('godPanel.attack'), 0xff8800, () => {
      if (!this.godPreviewEnemy?.alive) return;
      const enemy = this.godPreviewEnemy;
      const ex = enemy.body.x;
      const ey = enemy.body.y;
      const lungeX = ex - 15;
      const origX = ex;
      this.scene.tweens.add({
        targets: enemy.body,
        x: lungeX, duration: 80, ease: 'Power2', yoyo: true,
        onComplete: () => { if (enemy.alive) enemy.body.setPosition(origX, ey); },
      });
      if (enemy.weaponDefs.length > 0) {
        const targetX = ex - 80;
        const trail = this.scene.add.graphics().setDepth(26);
        trail.lineStyle(2, 0xff4444, 0.8);
        trail.lineBetween(ex, ey, targetX, ey + (Math.random() - 0.5) * 10);
        trail.fillStyle(0xff2222, 0.9);
        trail.fillCircle(targetX, ey, 4);
        this.scene.tweens.add({
          targets: trail, alpha: 0,
          duration: 300, onComplete: () => trail.destroy(),
        });
      }
      SoundManager.play('hit');
      JuiceEffects.screenShake(this.scene, 0.002, 40);
    });
    ebtnY += btnGap;
    this.addPreviewButton(enemyX, ebtnY, 'CLOAK', 0x8844ff, () => {
      if (!this.godPreviewEnemy?.alive) return;
      if (this.godPreviewEnemy.isCloaked) {
        this.godPreviewEnemy.isCloaked = false;
        this.godPreviewEnemy.body.setAlpha(1);
        this.godPreviewEnemy.hpBar.setVisible(true);
        this.godPreviewEnemy.hpBarBg.setVisible(true);
      } else {
        this.godPreviewEnemy.isCloaked = true;
        this.godPreviewEnemy.body.setAlpha(0.15);
        this.godPreviewEnemy.hpBar.setVisible(false);
        this.godPreviewEnemy.hpBarBg.setVisible(false);
      }
    });
    ebtnY += btnGap;
    this.addPreviewButton(enemyX, ebtnY, t('godPanel.destroy'), TECH.DANGER, () => {
      if (!this.godPreviewEnemy?.alive) return;
      const ex = this.godPreviewEnemy.body.x;
      const ey = this.godPreviewEnemy.body.y;
      this.godPreviewEnemy.destroy();
      JuiceEffects.deathBurst(this.scene, ex, ey, 0xff6644);
      SoundManager.play('enemyDeath');
    });
    ebtnY += btnGap;
    this.addPreviewButton(enemyX, ebtnY, t('godPanel.respawn'), TECH.COLOR, () => {
      this.respawnPreviewEnemy();
    });
  }

  private addPreviewButton(x: number, y: number, label: string, color: number, onClick: () => void): void {
    if (!this.godPreviewContainer) return;
    const btn = createSmallTechButton(this.scene, x, y, label, color, onClick);
    this.godPreviewContainer.add(btn);
  }

  private buildCycleSelector(x: number, y: number, getLabel: () => string, onChange: (dir: number) => void): void {
    if (!this.godPreviewContainer) return;
    const labelText = this.scene.add.text(x, y, getLabel(), {
      fontSize: '9px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.godPreviewContainer.add(labelText);

    const arrowStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '12px', color: '#88aaff', fontFamily: 'monospace', fontStyle: 'bold',
    };

    const leftArrow = this.scene.add.text(x - 60, y, '◄', arrowStyle)
      .setOrigin(0.5).setInteractive({ useHandCursor: true });
    const rightArrow = this.scene.add.text(x + 60, y, '►', arrowStyle)
      .setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.godPreviewContainer.add(leftArrow);
    this.godPreviewContainer.add(rightArrow);

    leftArrow.on('pointerdown', () => {
      SoundManager.play('clickCycle');
      onChange(-1);
      labelText.setText(getLabel());
    });
    rightArrow.on('pointerdown', () => {
      SoundManager.play('clickCycle');
      onChange(1);
      labelText.setText(getLabel());
    });
  }

  private spawnPreviewRobot(x: number, y: number): void {
    const designs = this.getPreviewDesigns();
    if (designs.length === 0) return;
    const design = designs[this.godPreviewDesignIdx % designs.length];
    const robot = new Robot(this.scene, x, y);
    for (const c of design.components) {
      robot.addComponent(c.type, c.lx, c.ly, c.rotation ?? 0, c.flipX ?? false, c.flipY ?? false, c.evoLevel ?? 0);
    }
    robot.recalcStats();
    robot.normalizeSize();
    const ps = GodModePanel.PREVIEW_SCALE;
    robot.container.setScale(robot.container.scaleX * ps, robot.container.scaleY * ps);
    robot.updateDebugOverlays();
    this.godPreviewRobot = robot;
  }

  private respawnPreviewRobot(): void {
    if (this.godPreviewRobot) {
      this.godPreviewRobot.destroy();
      this.godPreviewRobot = null;
    }
    this.spawnPreviewRobot(this.previewRobotX, this.previewY);
  }

  private spawnPreviewEnemy(x: number, y: number): void {
    const wp = [{ x, y }, { x, y }];
    const enemy = new Enemy(this.scene, wp, this.godPreviewEnemyType, 0, this.godPreviewEnemyGrade, true);
    enemy.previewScale = GodModePanel.PREVIEW_SCALE;
    enemy.body.setScale(enemy.body.scaleX * enemy.previewScale, enemy.body.scaleY * enemy.previewScale);
    this.godPreviewEnemy = enemy;
  }

  private respawnPreviewEnemy(): void {
    if (this.godPreviewEnemy) {
      this.godPreviewEnemy.destroy();
      this.godPreviewEnemy = null;
    }
    this.spawnPreviewEnemy(this.previewEnemyX, this.previewY);
  }

  private cleanupGodPreviews(): void {
    if (this.godPreviewRobot) {
      this.godPreviewRobot.destroy();
      this.godPreviewRobot = null;
    }
    if (this.godPreviewEnemy) {
      this.godPreviewEnemy.destroy();
      this.godPreviewEnemy = null;
    }
    if (this.godPreviewContainer) {
      this.godPreviewContainer.destroy();
      this.godPreviewContainer = null;
    }
  }
}
